import React, { useState, useRef, useEffect } from 'react';
import { 
  XMarkIcon, 
  PlusIcon, 
  ChevronUpIcon, 
  ChevronDownIcon,
  PhotoIcon,
  DocumentTextIcon,
  TrashIcon,
  FolderIcon
} from '@heroicons/react/24/outline';
import {  useLocation, } from 'react-router-dom';
import { uploadFile, baseImageToOss, generateRandomCode } from '../utils/common';
import { OSS_BUCKET, OSS_BASE_URL } from '../config';
import { type Resource, getGlobalResources, subscribeToResources } from '../utils/globalState';

interface ResourcesPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onChangeResource?: (resources: Resource[]) => void;
  // 显示模式：overlay（带遮罩固定定位）或 anchored（根据传入坐标定位，无遮罩）
  variant?: 'overlay' | 'anchored';
  // 当 variant 为 anchored 时使用的定位坐标（视口坐标）
  anchorPosition?: { left: number; top: number };
}

const ResourcesPopup: React.FC<ResourcesPopupProps> = ({ 
  isOpen, 
  onClose,
  onChangeResource,
  variant = 'overlay',
  anchorPosition
}) => {
  const [resources, setResources] = useState<Resource[]>(getGlobalResources());
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();

 
  
  // 监听全局资源变化
  useEffect(() => {
    // 初始化时先同步一次全局资源，确保首次渲染与全局一致
    setResources(getGlobalResources());

    // 订阅全局资源更新：当 updateGlobalResources 被调用时，这里会收到变更
    const unsubscribe = subscribeToResources((newResources: Resource[]) => {
      setResources(newResources);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  if (!isOpen) return null;

  const updateResources = (newResources: Resource[]) => {
    setResources(newResources);
    if (onChangeResource) {
      onChangeResource(newResources);
    }
  };

  // 获取文件扩展名的辅助函数
  const getFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return '.jpg'; // 默认扩展名
    }
    return filename.substring(lastDotIndex);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      try {
        const newResources: Resource[] = [];
        
        // 创建所有上传Promise
        const uploadPromises = Array.from(files).map(async (file, index) => {
          // 检查是否为图片文件
          if (!file.type.startsWith('image/')) {
            console.warn(`Skipping non-image file: ${file.name}`);
            return null;
          }
          
          try {
            // 将文件转换为base64
            const base64String = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (event) => {
                resolve(event.target?.result as string);
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
            
            try {
              // 调用 baseImageToOss 上传到OSS，使用实际文件扩展名
              const uploadedName = await baseImageToOss(
                generateRandomCode() + getFileExtension(file.name), 
                base64String, 
                OSS_BUCKET
              );
              
              console.log('上传成功:', uploadedName);
              
              return {
                id: `upload-${Date.now()}-${index}`,
                type: 'image' as const,
                content: `${OSS_BASE_URL}/${uploadedName}`,
                name: file.name,
                order: resources.length + index + 1
              };
              
            } catch (uploadError) {
              console.error('上传到OSS失败:', uploadError);
              return null;
            }
            
          } catch (error) {
            console.error(`文件处理失败 ${file.name}:`, error);
            return null;
          }
        });
        
        // 等待所有文件上传完成
        const results = await Promise.all(uploadPromises);
        
        // 过滤掉null结果并添加到newResources
        results.forEach(resource => {
          if (resource !== null) {
            newResources.push(resource);
          }
        });
        
        // 所有上传完成后，一次性更新资源
        console.log('newResources', newResources);
        if (newResources.length > 0) {
          updateResources([...resources, ...newResources]);
        }
        
      } catch (error) {
        console.error('Upload failed:', error);
        alert('Upload failed. Please try again.');
      }
    }
  };

  const handleAddText = () => {
    const newText: Resource = {
      id: `text-${Date.now()}`,
      type: 'text',
      content: 'New Text Content',
      name: 'New Text',
      order: resources.length + 1
    };
    updateResources([...resources, newText]);
  };

  const handleEditText = (id: string, newContent: string) => {
    const updatedResources = resources.map(resource => 
      resource.id === id 
        ? { ...resource, content: newContent, name: newContent.substring(0, 20) + (newContent.length > 20 ? '...' : '') }
        : resource
    );
    updateResources(updatedResources);
  };
 

  const deleteResource = (id: string) => {
    const updatedResources = resources.filter(r => r.id !== id);
    updateResources(updatedResources);
  };

  // 切换资源的 focus 标记
  const toggleResourceFocus = (id: string) => {
    const target = resources.find((r) => r.id === id);
    const updatedResources = resources.map((r) => {
      if (r.id === id) {
        return { ...(r as any), focus: !((r as any).focus === true) };
      }
      // 当切换的是 image 类型时，其它 image 的 focus 置为 false（互斥）
      if (target && (target as any).type === 'image' && r.type === 'image') {
        return { ...(r as any), focus: false };
      }
      return r;
    });
    if (location.pathname.includes('/product/')) {
      try {
        const applyFn = (globalThis as any).__GiftMkr_onFrameImageChange__;
        if (applyFn && target && (target as any).type === 'image') {
          const url = (target as any).content;
          const aiCode = (target as any).ai ?? '';
          // 默认第一个图层，通过全局桥接函数在 ProductDetail 中解析
          applyFn('first', url, aiCode);
        }
      } catch (err) {
        console.warn('Apply image via global onFrameImageChange failed:', err);
      }
    } else if (location.pathname.includes('/customize/')) {
      try {
        const applyFn = (globalThis as any).__GiftMkr_applyResourceToSelectedLayer__;
        if (applyFn && target && (target as any).type === 'image') {
          const url = (target as any).content;
          const aiCode = (target as any).ai ?? '';
          applyFn(url, aiCode);
        }
      } catch (err) {
        console.warn('Apply image to customize selected layer failed:', err);
      }
    }
    updateResources(updatedResources);
  };

  // anchored 模式：不渲染遮罩，使用传入坐标进行 fixed 定位
  if (variant === 'anchored') {
    return (
      <div
        className="fixed z-50"
        style={{ left: anchorPosition?.left ?? 0, top: anchorPosition?.top ?? 0 }}
      >
        <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-md md:w-96 max-h-[80vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">
             Bulk Custom Resources
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Close resources"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[70vh]">
            {/* Add Buttons */}
            <div className="mb-4 flex items-center space-x-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center justify-center w-10 h-10 text-white rounded-full transition-colors"
                style={{ backgroundColor: '#2e3a47' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                aria-label="Upload images"
                title="Upload images"
              >
                <PhotoIcon className="h-5 w-5" />
              </button>
              
              <button
                onClick={handleAddText}
                className="flex items-center justify-center w-10 h-10 text-white rounded-full transition-colors"
                style={{ backgroundColor: '#68aeb0' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7bb8ba'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#68aeb0'}
                aria-label="Add text"
                title="Add text"
              >
                <DocumentTextIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />

            {/* Resources List */}
            <div className="space-y-2">  
              {resources.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p
                    className='text-sm font-semibold text-red-600'
                  >
                   Please upload images or enter text to start customizing.
                  </p>
                </div>
              ) : (
                resources.map((resource) => (
                  <div
                    key={resource.id}
                    className={`flex items-center space-x-3 p-3 rounded-lg transition-colors cursor-pointer border 
                      ${((resource as any)?.focus === true) 
                        ? 'bg-gray-300 hover:bg-gray-400 border-gray-500' 
                        : 'bg-gray-50 hover:bg-gray-100 border-gray-200'}`}
                      onClick={(e) => { e.stopPropagation(); toggleResourceFocus(resource.id); }}
                  >
                    {/* Resource Preview */}
                    <div className="flex-shrink-0">
                      {resource.type === 'image' ? (
                        <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center overflow-hidden">
                          <img
                            src={resource.aiUrl?resource.aiUrl:resource.content}
                            alt={resource.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                          <DocumentTextIcon className="h-6 w-6 text-gray-500" />
                        </div>
                      )}
                    </div>

                    {/* Resource Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <div className="flex flex-col">
                          <h4 className="text-sm font-medium text-gray-900 truncate" title={resource.name}>
                            {resource.name.length > 30 ? resource.name.substring(0, 20) + '...' : resource.name}
                          </h4>
                          {(resource as any)?.ai ? (
                            <p className="text-xs text-gray-500 mt-1 break-words leading-snug">
                              {(resource as any).ai}
                            </p>
                          ) : null}
                        </div>
                        <span 
                          className={`text-xs px-2 py-1 rounded-full ${
                            resource.type === 'image' 
                              ? '' 
                              : 'bg-green-100 text-green-700'
                          }`}
                          style={resource.type === 'image' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                    
                          title="Toggle focus"
                        >
                          {resource.type} 
                        </span>
                      </div>
                      {resource.type === 'text' && (
                        <div className="mt-1">
                          <textarea
                            value={resource.content}
                            onChange={(e) => handleEditText(resource.id, e.target.value)}
                            className="w-full text-xs text-gray-600 border border-gray-200 rounded px-2 py-1 resize-none"
                            rows={2}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="Enter text content..."
                          />
                        </div>
                      )}
                    </div>

                    {/* Controls */}
                    <div className="flex items-center space-x-1"> 
                      {/* Delete */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteResource(resource.id);
                        }}
                        className="p-1 text-red-400 hover:text-red-600 transition-colors"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // overlay 模式：保留现有遮罩与固定定位
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />
      
      {/* Resources Popup */}
      <div className="fixed top-20 right-0 md:right-8 lg:right-12 z-50">
        <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-md md:w-96 max-h-[80vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">
              Custom Resources
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Close resources"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[70vh]">
            {/* Add Buttons */}
            <div className="mb-4 flex items-center space-x-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center justify-center w-10 h-10 text-white rounded-full transition-colors"
                style={{ backgroundColor: '#2e3a47' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                aria-label="Upload images"
                title="Upload images"
              >
                <PhotoIcon className="h-5 w-5" />
              </button>
              
              <button
                onClick={handleAddText}
                className="flex items-center justify-center w-10 h-10 text-white rounded-full transition-colors"
                style={{ backgroundColor: '#68aeb0' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7bb8ba'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#68aeb0'}
                aria-label="Add text"
                title="Add text"
              >
                <DocumentTextIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />

            {/* Resources List */}
            <div className="space-y-2">
              {resources.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FolderIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-sm">
                    You can upload images or enter text to customize gifts in bulk
                  </p>
                </div>
              ) : (
                resources.map((resource) => (
                  <div
                    key={resource.id}
                    className={`flex items-center space-x-3 p-3 rounded-lg transition-colors cursor-pointer border 
                      ${((resource as any)?.focus === true) 
                        ? 'bg-gray-300 hover:bg-gray-400 border-gray-500' 
                        : 'bg-gray-50 hover:bg-gray-100 border-gray-200'}`}

                  >
                    {/* Resource Preview */}
                    <div className="flex-shrink-0">
                      {resource.type === 'image' ? (
                        <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center overflow-hidden">
                          <img
                            src={resource.aiUrl?resource.aiUrl:resource.content}
                            alt={resource.name}  
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                          <DocumentTextIcon className="h-6 w-6 text-gray-500" />
                        </div>
                      )}
                    </div>

                    {/* Resource Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-sm font-medium text-gray-900 truncate" title={resource.name}>
                          {resource.name.length > 30 ? resource.name.substring(0, 20) + '...' : resource.name}
                        </h4>
                        <span 
                          className={`text-xs px-2 py-1 rounded-full ${
                            resource.type === 'image' 
                              ? '' 
                              : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {resource.type}  
                        </span>
                      </div>
                      {resource.type === 'text' && (
                        <textarea
                          className="mt-1 w-full text-sm border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
                          value={resource.content}
                          onChange={(e) => handleEditText(resource.id, e.target.value)}
                          rows={2}
                        />
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2">
                      {/* <button
                        className="p-2 text-gray-500 hover:text-gray-700"
                        onClick={() => moveResource(resource.id, 'up')}
                        aria-label="Move up"
                        title="Move up"
                      >
                        <ChevronUpIcon className="h-4 w-4" />
                      </button>
                      <button
                        className="p-2 text-gray-500 hover:text-gray-700"
                        onClick={() => moveResource(resource.id, 'down')}
                        aria-label="Move down"
                        title="Move down"
                      >
                        <ChevronDownIcon className="h-4 w-4" />
                      </button> */}
                      <button
                        className="p-2 text-red-500 hover:text-red-700"
                        onClick={() => deleteResource(resource.id)}
                        aria-label="Delete"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResourcesPopup;
